// src/neetjee_guru/components/academicPlanner/TimelineViewport.js
import React, { Component } from "react";

// Subcomponent for individual day card, optimized to prevent re-renders
class TimelineDayCard extends Component {
  shouldComponentUpdate(nextProps) {
    const { day, activeSubjects, isSelected, searchText } = this.props;
    return (
      nextProps.day.date !== day.date ||
      nextProps.day.type !== day.type ||
      nextProps.day.title !== day.title ||
      nextProps.day.topics !== day.topics ||
      nextProps.isSelected !== isSelected ||
      nextProps.searchText !== searchText ||
      JSON.stringify(nextProps.activeSubjects) !== JSON.stringify(activeSubjects)
    );
  }

  render() {
    const { day, activeSubjects, isSelected, onDayClick, streamName } = this.props;

    // Determine the subject columns to render dynamically matching live database keys
    let subjectsToRender = [];
    if (day.topics && Object.keys(day.topics).length > 0) {
      const dbSubjects = Object.keys(day.topics);
      if (activeSubjects && activeSubjects.length > 0) {
        subjectsToRender = dbSubjects.filter(s => 
          activeSubjects.includes(s) || 
          activeSubjects.some(a => s.toLowerCase().includes(a.toLowerCase()))
        );
        if (subjectsToRender.length === 0) {
          subjectsToRender = dbSubjects;
        }
      } else {
        subjectsToRender = dbSubjects;
      }
    } else {
      subjectsToRender = activeSubjects && activeSubjects.length > 0 ? activeSubjects : [];
    }

    // Color-coding determinations
    let cardClass = "timeline-day-card ds-surface ";
    let typeBadgeClass = "ds-pill ";
    let dotColor = "";

    if (day.type === "Exam") {
      cardClass += "card-exam-day";
      typeBadgeClass += "ds-pill--info";
      dotColor = "var(--ds-info)";
    } else if (day.type === "Holiday") {
      cardClass += "card-holiday-day";
      typeBadgeClass += "ds-pill--crit";
      dotColor = "var(--ds-crit)";
    } else {
      cardClass += "card-lesson-day";
      typeBadgeClass += "ds-pill--mute";
      dotColor = "var(--ds-brand-400)";
    }

    if (isSelected) {
      cardClass += " is-selected";
    }

    const todayDateObj = new Date();
    const todayYear = todayDateObj.getFullYear();
    const todayMonth = String(todayDateObj.getMonth() + 1).padStart(2, '0');
    const todayDay = String(todayDateObj.getDate()).padStart(2, '0');
    const todayStr = `${todayYear}-${todayMonth}-${todayDay}`;
    const isToday = day.date === todayStr || day.isToday === true;

    // Format date string beautifully
    const formattedDate = new Date(day.date).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });

    return (
      <div
        id={`day-row-${day.date}`}
        className={`${cardClass} ${isToday ? "today-target-row" : ""}`}
        onClick={() => onDayClick(day)}
        style={{
          borderRadius: 14,
          border: isToday ? "2px solid #7c3aed" : "1px solid #e2e8f0",
          boxShadow: isToday ? "0 4px 16px rgba(124, 58, 237, 0.15)" : "0 2px 8px rgba(0,0,0,0.03)",
          background: isToday ? "linear-gradient(135deg, #ffffff 0%, #faf5ff 100%)" : "#ffffff",
          marginBottom: 14,
          transition: "all 0.2s ease"
        }}
      >
        {isToday && <div className="today-badge-indicator">TODAY'S SCHEDULE</div>}

        <div className="card-body-wrapper p-3 d-flex flex-column flex-md-row justify-content-between gap-3">
          {/* Left panel: Date & Event Label */}
          <div className="day-card-left flex-shrink-0" style={{ width: "200px" }}>
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="font-weight-bold text-dark" style={{ fontSize: "15px" }}>{day.dayName}</span>
              {day.dayNumber && (
                <span className="badge badge-light border text-muted" style={{ fontSize: "10px" }}>
                  Day {day.dayNumber}
                </span>
              )}
            </div>
            <div className="text-muted small mb-2">{formattedDate}</div>
            
            <div className="d-flex flex-column gap-1 align-items-start">
              <span
                className="badge"
                style={{
                  fontSize: "11px",
                  padding: "4px 10px",
                  borderRadius: 12,
                  background: day.type === "Exam" ? "#e0f2fe" : day.type === "Holiday" ? "#fee2e2" : "#f1f5f9",
                  color: day.type === "Exam" ? "#0369a1" : day.type === "Holiday" ? "#b91c1c" : "#475569",
                  fontWeight: 700
                }}
              >
                <i className={`fal ${day.type === "Exam" ? "fa-file-alt" : day.type === "Holiday" ? "fa-umbrella-beach" : "fa-book-open"} mr-1`} />
                {day.subType || day.type}
              </span>
            </div>
          </div>

          {/* Right panel: Content Columns */}
          <div className="day-card-right flex-grow-1">
            {day.type !== "Regular" && (
              <div className="special-day-description p-3 rounded-lg mb-3"
                style={{
                  background: day.type === "Exam" ? "#f0f9ff" : "#fef2f2",
                  borderLeft: `4px solid ${day.type === "Exam" ? "#0284c7" : "#ef4444"}`,
                  borderRadius: 8
                }}
              >
                <h6 className="font-weight-bold m-0" style={{ color: day.type === "Exam" ? "#0369a1" : "#991b1b" }}>
                  {day.title}
                </h6>
                {day.description && <p className="text-muted small m-0 mt-1">{day.description}</p>}
              </div>
            )}

            {/* Render subject cards as columns */}
            {subjectsToRender.length > 0 ? (
              <div className="row">
                {subjectsToRender.map(subject => {
                  const topicText = (day.topics && day.topics[subject]) ? day.topics[subject] : "";
                  const status = (day.teachingStatus && day.teachingStatus[subject]) || "PENDING";
                  
                  const s = subject.trim().toLowerCase();
                  let borderColor = "#64748b";
                  let bgTint = "#f8fafc";
                  
                  if (s.includes("math")) { borderColor = "#8b5cf6"; bgTint = "#faf5ff"; }
                  else if (s.includes("physic") || s.includes("phy")) { borderColor = "#3b82f6"; bgTint = "#eff6ff"; }
                  else if (s.includes("chemist") || s.includes("chem")) { borderColor = "#10b981"; bgTint = "#f0fdf4"; }
                  else if (s.includes("botan") || s.includes("bot")) { borderColor = "#059669"; bgTint = "#ecfdf5"; }
                  else if (s.includes("zoolog") || s.includes("zoo")) { borderColor = "#f59e0b"; bgTint = "#fffbeb"; }

                  return (
                    <div key={subject} className="col-12 col-md-6 col-lg-4 mb-2">
                      <div
                        className="subject-topic-card p-3 h-100 rounded"
                        style={{
                          background: bgTint,
                          borderLeft: `3px solid ${borderColor}`,
                          borderRadius: 10,
                          boxShadow: "0 1px 4px rgba(0,0,0,0.02)"
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <span className="text-uppercase small font-weight-bold text-muted" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>
                            {subject}
                          </span>
                          <span
                            className="badge"
                            style={{
                              fontSize: "9.5px",
                              padding: "2px 8px",
                              borderRadius: 10,
                              background: status === "COMPLETED" ? "#d1fae5" : status === "IN_PROGRESS" ? "#fef3c7" : "#e2e8f0",
                              color: status === "COMPLETED" ? "#065f46" : status === "IN_PROGRESS" ? "#92400e" : "#475569",
                              fontWeight: 700
                            }}
                          >
                            {status === "COMPLETED" ? "✓ Done" : status === "IN_PROGRESS" ? "⏳ In Progress" : "Pending"}
                          </span>
                        </div>

                        <div className="subject-topic-text text-dark font-weight-bold small mt-1" style={{ lineHeight: 1.4 }}>
                          {topicText || <span className="text-muted font-weight-normal italic">No topic assigned for this day</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              day.type === "Regular" && (
                <div className="text-muted small italic py-2">
                  No active subjects selected. Enable subject isolation pills above.
                </div>
              )
            )}
          </div>
        </div>
      </div>
    );
  }
}

class TimelineViewport extends Component {
  render() {
    const { filteredDays, activeSubjects, onDayClick, selectedDay, searchText, streamName } = this.props;

    if (filteredDays.length === 0) {
      return (
        <div className="text-center p-5 ds-surface-card rounded-lg border border-light shadow-sm bg-white my-4">
          <i className="fal fa-file-csv display-4 text-primary mb-3" />
          <h5 className="font-weight-bold text-dark">
            {searchText ? "No schedule matches search filter" : "No Syllabus Entries Seeded Yet"}
          </h5>
          <p className="text-muted small max-w-md mx-auto">
            {searchText
              ? `No topics match "${searchText}". Try clearing your search keyword or date filters.`
              : "This master planner does not have any topic schedules inserted yet. Seed your curriculum syllabus via bulk CSV upload."}
          </p>
          {!searchText && this.props.onOpenBulkUpload && (
            <button
              className="btn btn-primary btn-sm px-3 mt-2 shadow-sm font-weight-bold"
              onClick={this.props.onOpenBulkUpload}
            >
              <i className="fal fa-file-upload mr-1" /> Seed Syllabus via Bulk CSV Upload
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="timeline-viewport-wrapper">
        {filteredDays.map(day => {
          const isSelected = selectedDay && selectedDay.date === day.date;

          return (
            <TimelineDayCard
              key={day.date}
              day={day}
              activeSubjects={activeSubjects}
              isSelected={isSelected}
              onDayClick={onDayClick}
              searchText={searchText}
              streamName={streamName}
            />
          );
        })}
      </div>
    );
  }
}

export default TimelineViewport;
